import { GoPlus, GoX, GoChevronDown } from "react-icons/go";
import { useState, useEffect } from "react";
import { Combobox } from "@headlessui/react";

/**
 * Displays the Keyword filter section.
 *
 * @param {*} props Contains 'filters' state variable & 'setFilters' state method.
 */
export default function KeywordFilter(props) {
  const [inputValue, setInputValue] = useState("");
  const [chips, setChips] = useState(null);
  const [selectedKeyword, setSelectedKeyword] = useState("");
  const [allowInputValueChange, setAllowInputValueChange] = useState(true);
  const [keywordChoices, setKeywordChoices] = useState([]);

  const keywordFilters = props.filters.keywords;
  const setKeywordFilters = props.setFilters;

  // Load keyword choices from localStorage and watch for updates
  useEffect(() => {
    const loadKeywordChoices = () => {
      try {
        const attributesStr = localStorage.getItem("attributes");
        if (attributesStr) {
          const attributesData = JSON.parse(attributesStr);
          console.log('Loading keyword choices from localStorage:', attributesData);
          if (
            attributesData &&
            attributesData.filterOptions &&
            Array.isArray(attributesData.filterOptions)
          ) {
            console.log('Total filterOptions:', attributesData.filterOptions.length);
            const keywords = attributesData.filterOptions
              .filter((filterOption) => filterOption.category === "keyword")
              .slice(0, 30);
            console.log('Filtered keywords:', keywords.length, keywords);
            setKeywordChoices(keywords);
          } else {
            console.log('No valid filterOptions found in localStorage');
          }
        } else {
          console.log('No attributes found in localStorage');
        }
      } catch (e) {
        console.error('Error parsing attributes from localStorage:', e);
      }
    };

    // Load immediately
    loadKeywordChoices();

    // Listen for custom event when attributes are updated
    const handleAttributesUpdate = () => {
      loadKeywordChoices();
    };
    window.addEventListener('localStorage:attributesUpdated', handleAttributesUpdate);

    // Listen for storage events (when localStorage is updated from another tab/window)
    const handleStorageChange = (e) => {
      if (e.key === 'attributes') {
        loadKeywordChoices();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    // Cleanup
    return () => {
      window.removeEventListener('localStorage:attributesUpdated', handleAttributesUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  //Takes keywordChoices and filters them based on what the user has entered into the keyword search input
  const filteredKeywordChoices =
    inputValue === ""
      ? keywordChoices
      : keywordChoices.filter((keywordChoice) => {
        return keywordChoice.name.toLowerCase().includes(inputValue.toLowerCase());
      });
  //Populates the combobox input with the user's keyword selection from the combobox dropdown
  useEffect(() => {
    if (
      typeof selectedKeyword !== "undefined" &&
      selectedKeyword !== "" &&
      selectedKeyword !== null
    ) {
      // Without this, when you select an item from the dropdown, it adds a chip for the item but then also populates the input (bad). 
      // This makes it so the chip is added and the input remains empty by checking if input value is allowed to be added.
      if (allowInputValueChange) {
        setInputValue(selectedKeyword.name);
      }
      setAllowInputValueChange(true);
    }
  }, [selectedKeyword]);

  // Everytime the keywords in the 'filters' state are updated it re-renders the chips
  useEffect(() => {
    displayChips();
  }, [keywordFilters]);

  /**
   * Iterates through the keyword property of the 'filters' state object to create chips and stores them in 'chips' state variable.
   */
  function displayChips() {
    if (keywordFilters !== "") {
      setChips(
        keywordFilters.map((keywordFilter, index) => {
          return (
            <span
              key={keywordFilter || `keyword-${index}`}
              className="break-all mr-1.5 mt-1.5 px-3 py-2 rounded-lg text-white bg-blue-700 font-semibold text-sm flex items-center w-max cursor-pointer active:bg-gray-300 transition duration-300 ease hover:bg-blue-600"
              onClick={() => {
                deleteKeyword(keywordFilter);
              }}
            >
              {keywordFilter}
              <GoX className="h-4 w-4 text-white ml-2" aria-hidden="true" />
            </span>
          );
        })
      );
    }
  }

  /**
   * Adds a keyword to the 'filters.keywords' state variable
   */
  function addKeyword(keyword) {
    setSelectedKeyword("");
    let trimmedInputValue;

    if (keyword !== undefined) {
      trimmedInputValue = keyword.trim();
    } else {
      trimmedInputValue = JSON.parse(JSON.stringify(inputValue)).trim()
    }
    if (trimmedInputValue !== "") {
      setKeywordFilters((prevState) => {
        const filters = {
          ...prevState,
          keywords: [...prevState.keywords, trimmedInputValue],
        }

        props.setParam("keywords", filters.keywords);
        return filters;
      });
      setInputValue("");
    }
  }

  /**
   * Removes the specified keyword from the 'filters' state variable.
   *
   * @param {*} keyword Keyword to remove.
   */
  function deleteKeyword(keyword) {
    let updatedKeywordsFilter = [...keywordFilters];
    updatedKeywordsFilter.splice(updatedKeywordsFilter.indexOf(keyword), 1);

    setKeywordFilters((prevState) => {
      const filters = {
        ...prevState,
        keywords: updatedKeywordsFilter,
      }

      if (!filters.keywords.length) {
        props.removeParam("keywords");
      }
      else {
        props.setParam("keywords", filters.keywords);
      }

      return filters;
    });
  }

  //Joins strings. Used for combobox option hover styles.
  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <Combobox
          as="div"
          className="w-full sm:max-w-xs"
          value={selectedKeyword}
          onChange={setSelectedKeyword}
          nullable
        >
          <div className="relative">
            <Combobox.Input
              value={inputValue}
              data-cy="keywords"
              className="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
              onChange={(event) => {
                setInputValue(event.target.value);
              }}
              displayValue={inputValue}
              maxLength={250}
              placeholder="Search Keywords"
              onKeyDown={(e) => {
                // e.key === 'Enter' ? addKeyword() : null; 
                if (e.key === 'Enter') {
                  addKeyword();
                }
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                  setInputValue("");
                }
              }}
            />

            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
              <GoChevronDown
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </Combobox.Button>

            {filteredKeywordChoices.length > 0 && (
              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm border dark:bg-gray-800 dark:border-gray-700">
                {filteredKeywordChoices.map((filteredKeywordChoice, index) => (
                  <Combobox.Option
                  onClick={(e) => {
                      addKeyword(filteredKeywordChoice.name)
                      setAllowInputValueChange(false);
                    }}
                    key={filteredKeywordChoice.PK}
                    value={filteredKeywordChoice}
                    className={({ active }) =>
                    classNames(
                      "relative cursor-default select-none py-2 pl-3 pr-9",
                      active ? "bg-blue-600 text-white" : "text-gray-900 dark:text-gray-200"
                      )
                    }
                    >
                    <span>{filteredKeywordChoice.name}</span>
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            )}
          </div>
        </Combobox>

        <button
          disabled={inputValue.length === 0 ? true : false}
          data-cy="keyword-add"
          type="button"
          className={
            classNames(
              "w-full mt-2 inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm",
              inputValue.length === 0 ? "bg-gray-300 cursor-default dark:bg-gray-800" : "bg-blue-600 hover:bg-blue-700"
            )
          }
          onClick={() => addKeyword()}
        >
          <span className="sr-only">Add keyword</span>
          <GoPlus className={classNames("h-4 w-4 text-white", inputValue.length === 0 ? "dark:text-gray-400" : "dark:text-white")} aria-hidden="true" />
        </button>
      </div>

      {/* Displays keyword chips */}
      <div className="mt-5 flex flex-wrap">{chips}</div>
    </div>
  );
}